/**
 * Pandora media URL construction + direct-vs-proxy resolution.
 *
 * Rules (docs/pandora-api.md + timeline-format.md):
 *  - Streams/canvas-drawn media: direct `{site}/{item}/{R}p{N}.{fmt}` when the
 *    archive serves CORS on media (pad.ma), otherwise through the backend
 *    proxy `/api/proxy/{archive}/{item}/{filename}`.
 *  - Display-only images (<img>, CSS backgrounds) never need CORS → direct.
 */
import type { Archive } from '../api/archives';
import type { PandoraVideoSource } from '../format/types';

export function maxResolution(archive: Archive): number {
  return Math.max(...archive.resolutions);
}

export function preferredFormat(archive: Archive): string {
  // mp4 is the guaranteed stream format on all instances (webm not always
  // present) and decodes everywhere incl. WebCodecs — prefer it.
  return archive.formats.includes('mp4') ? 'mp4' : archive.formats[0];
}

/**
 * The resolution+format a stream URL will actually use. Single source of truth
 * so the clip cache keys on exactly what the preview loads — if these diverged
 * the cache would silently never hit.
 */
export function mediaParams(
  archive: Archive,
  opts: { resolution?: number; format?: string } = {},
): { resolution: number; format: string } {
  return {
    resolution: opts.resolution ?? maxResolution(archive),
    format: opts.format ?? preferredFormat(archive),
  };
}

/** Stream URL for canvas/WebCodecs/<video> use — proxied when media lacks CORS. */
export function mediaUrl(
  archive: Archive,
  item: string,
  part: number,
  opts: { resolution?: number; format?: string } = {},
): string {
  const res = opts.resolution ?? maxResolution(archive);
  const fmt = opts.format ?? preferredFormat(archive);
  const filename = `${res}p${part}.${fmt}`;
  if (archive.corsMedia) return `${archive.site}/${item}/${filename}`;
  return `/api/proxy/${archive.key}/${item}/${filename}`;
}

/** Resolve a pandora-video source's stream URL. */
export function sourceMediaUrl(source: PandoraVideoSource, archives: Archive[]): string | null {
  const archive = archives.find((a) => a.key === source.archive);
  if (!archive) return null;
  return mediaUrl(archive, source.item, source.part);
}

/** Poster image (display only → direct). */
export function posterUrl(archive: Archive, item: string): string {
  return `${archive.site}/${item}/poster.jpg`;
}

/**
 * Frame at t seconds (display only → direct). t may be fractional and is in
 * CONCATENATED item time (across all parts) — for part N add the sum of prior
 * part durations. Never 404s (generic fallback image served instead).
 */
export function frameUrl(
  archive: Archive,
  item: string,
  t: number,
  resolution?: number,
): string {
  const res = resolution ?? Math.min(...archive.resolutions);
  const tt = Math.round(t * 100) / 100;
  return `${archive.site}/${item}/${res}p${tt}.jpg`;
}

/** Frame URL routed for canvas use (image overlays need CORS). */
export function frameUrlForCanvas(
  archive: Archive,
  item: string,
  t: number,
  resolution?: number,
): string {
  const res = resolution ?? Math.max(...archive.resolutions);
  const tt = Math.round(t * 100) / 100;
  const filename = `${res}p${tt}.jpg`;
  if (archive.corsMedia) return `${archive.site}/${item}/${filename}`;
  return `/api/proxy/${archive.key}/${item}/${filename}`;
}

/**
 * Timeline strip tiles: `{site}/{item}/timeline16p{i}.jpg`, 16px tall,
 * 1px per second of CONCATENATED item time (all parts), tiled every 3600s
 * (verified: KDK = 4396.867s → tiles of 3600×16 and 797×16).
 * `timelineaudio16p{i}.jpg` is the waveform variant (audio clip blocks).
 */
export const TIMELINE_TILE_SECONDS = 3600;

export function timelineStripUrls(
  archive: Archive,
  item: string,
  duration: number,
  variant: 'timeline' | 'timelineaudio' = 'timeline',
): string[] {
  const n = Math.max(1, Math.ceil(duration / TIMELINE_TILE_SECONDS));
  const urls: string[] = [];
  for (let i = 0; i < n; i++)
    urls.push(`${archive.site}/${item}/${variant}16p${i}.jpg`);
  return urls;
}

export function timelineStripTileUrl(
  archive: Archive,
  item: string,
  tile: number,
  variant: 'timeline' | 'timelineaudio' = 'timeline',
): string {
  return `${archive.site}/${item}/${variant}16p${tile}.jpg`;
}
